import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import ArticleRewardManagementListing from '../../components/RegisterManagement/ArticleRewardManagement/ArticleRewardManagementListing';
import ArticleRewardManagementForm from '../../components/RegisterManagement/ArticleRewardManagement/ArticleRewardManagementForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const ArticleRewardManagement = () => {
    const toast = useRef(null);
    const todayStr = new Date().toISOString().split('T')[0];
    const initialForm = { Id: 0, UserId: '', Note: '', Notes: '', Status: 'Đã gửi duyệt', ArticleIdsJson: '[]', AdminRequest: '' };

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [formData, setFormData] = useState(initialForm);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [editRequestContent, setEditRequestContent] = useState('');
    const [selectedProposalForEdit, setSelectedProposalForEdit] = useState(null);

    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedRejectId, setSelectedRejectId] = useState(null);

    const [filters, setFilters] = useState({
        department: '',
        authorId: null,
        generalSearch: '',
        status: ''
    });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = (currentUser.RoleName || '').toLowerCase();

    const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa');

    const canModify = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            const [deptRes, userRes] = await Promise.all([
                fetch(`${baseUrl}/department`, { headers: authHeaders }),
                fetch(`${baseUrl}/user?mode=selection`, { headers: authHeaders })
            ]);
            if (deptRes.ok) setDepartments(await deptRes.json());
            if (userRes.ok) setUsers(await userRes.json());
        } catch (error) {
            console.error("Lỗi tải danh mục:", error);
        }
    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            if (!currentUser || !currentUser.Id) return;

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/articlereward?userId=${currentUser.Id}&isAdmin=${isManager}`,
                {
                    method: 'GET',
                    headers: authHeaders
                }
            );
            if (response.ok) {
                let result = await response.json();
                result = result.map(item => ({
                    ...item,
                    Notes: item.Notes || item.Note || ''
                }));
                setData(result);
            }
        } catch (error) {
            if (toast.current) toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser, isManager, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };

                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        return data.filter(item => {
            if (filters.status && item.Status !== filters.status) return false;

            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.authorId) {
                if (item.UserId?.toString() !== filters.authorId.toString()) {
                    return false;
                }
            }

            if (filters.generalSearch) {
                const query = superCleanString(filters.generalSearch);
                const note = superCleanString(item.Note);
                const notes = superCleanString(item.Notes);

                if (!note.includes(query) && !notes.includes(query)) {
                    return false;
                }
            }

            return true;
        });
    }, [data, filters, departments]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNew = () => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền thêm mới!', life: 3000 });
        }
        setFormData(initialForm);
        setIsReadOnly(false);
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép sửa!', life: 3000 });
        }
        if (item.Status === 'Đã duyệt') {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Hồ sơ đã duyệt, không thể sửa. Hãy hủy duyệt trước!', life: 4000 });
        }
        setFormData({
            ...item,
            Note: item.Note || '',
            Notes: item.Notes || item.Note || ''
        });
        setIsReadOnly(false);
        setIsModalOpen(true);
    };

    const handleViewDetail = (item) => {
        setFormData({
            ...item,
            Note: item.Note || '',
            Notes: item.Notes || item.Note || ''
        });
        setIsReadOnly(true);
        setIsModalOpen(true);
    };

    const handleStatusChange = async (id, newStatus) => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt/từ chối!', life: 3000 });
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${currentUser.Id}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ id: id, status: newStatus })
            });
            if (response.ok) {
                const messages = {
                    'Đã duyệt': "Phê duyệt thành công!",
                    'Đã gửi duyệt': "Đã hủy phê duyệt thành công!"
                };
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: messages[newStatus] || 'Cập nhật trạng thái thành công!', life: 3000 });
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    };

    const handleRejectClick = (id) => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền từ chối!', life: 3000 });
        }
        setSelectedRejectId(id);
        setRejectReason('');
        setIsRejectModalOpen(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!', life: 3000 });
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${currentUser.Id}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({
                    id: selectedRejectId,
                    status: 'Từ chối',
                    note: rejectReason
                })
            });
            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã từ chối hồ sơ!', life: 3000 });
                setIsRejectModalOpen(false);
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Từ chối thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleOpenRequestEdit = (item) => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedProposalForEdit(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    };

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập nội dung yêu cầu!', life: 3000 });
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${currentUser.Id}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({
                    id: selectedProposalForEdit.Id,
                    status: 'Yêu cầu chỉnh sửa',
                    note: editRequestContent
                })
            });

            if (response.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã gửi Email yêu cầu chỉnh sửa!', life: 3000 });
                setIsEditRequestModalOpen(false);
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Gửi yêu cầu thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleDelete = (id) => {
        if (!canModify) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }

        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã duyệt') {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa hồ sơ đã duyệt!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa hồ sơ xét thưởng này khỏi hệ thống?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?id=${id}&isAdmin=${isManager}&userId=${currentUser.Id}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa dữ liệu!' });
                        fetchData(true);
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
                    }
                } catch (e) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?isAdmin=${isManager}&userId=${currentUser.Id}`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                    body: JSON.stringify({ id, status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} hồ sơ đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?id=${id}&isAdmin=${isManager}&userId=${currentUser.Id}`, {
                            method: 'DELETE',
                            headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const executeExport = (dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const formatVNToISO = (dateStr) => {
            if (!dateStr) return '';
            const datePart = dateStr.split(' ')[0];
            const parts = datePart.split('/');
            if (parts.length === 3) {
                return `${parts[0]}/${parts[1]}/${parts[2]}`;
            }
            return dateStr;
        };

        const formattedData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Giảng viên đề xuất': item.FullName || item.RequesterName || '',
            'Đơn vị': item.DepartmentName || '',
            'Số lượng bài báo': item.ArticleCount || 0,
            'Tổng tiền đề xuất (VNĐ)': item.TotalAmount || 0,
            'Ghi chú': item.Notes || item.Note || '',
            'Trạng thái': item.Status || '',
            'Ngày cập nhật': formatVNToISO(item.UpdatedDate || item.CreatedDate || item.UpdatedAt)
        }));

        const summaryRow = {
            'STT': '', 'Giảng viên đề xuất': 'TỔNG CỘNG:', 'Đơn vị': `${dataToExport.length} Hồ sơ`,
            'Số lượng bài báo': dataToExport.reduce((sum, i) => sum + (i.ArticleCount || 0), 0),
            'Tổng tiền đề xuất (VNĐ)': dataToExport.reduce((sum, i) => sum + (i.TotalAmount || 0), 0),
            'Ghi chú': '', 'Trạng thái': '', 'Ngày cập nhật': ''
        };

        const colWidths = [
            { wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
            { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `QuanLyXetThuong_${fileSuffix}`,
            sheetName: 'QuanLyXetThuong',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    };

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = () => {
        let result = [...data];

        if (exportFilters.userId) {
            result = result.filter(item => item.UserId?.toString() === exportFilters.userId.toString());
        }

        return result;
    };

    const handlePreviewAdvancedFilters = () => {
        const result = getFilteredExportData();
        setPreviewExportData(result);
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        const result = previewExportData || getFilteredExportData();
        executeExport(result, 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Name}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />

            <div className="page-header d-flex justify-content-between align-items-center mb-4">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ HỖ TRỢ CÔNG BỐ KHOA HỌC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                {canModify && (
                    <button
                        className="btn-add-new"
                        onClick={handleAddNew}
                        style={{ backgroundColor: '#0056b3', margin: 0, width: isDesktop ? 'auto' : '100%' }}
                    >
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <p className="sub-title" style={{ marginBottom: '15px' }}>DANH SÁCH HỒ SƠ</p>

                <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr 1fr' : '1fr', gap: '15px', marginBottom: '15px' }}>
                    <select name="department" className="form-input" value={filters.department} onChange={handleFilterChange}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <div style={{ position: 'relative' }}>
                        <input type="text" name="generalSearch" className="form-input" style={{ width: '100%', paddingRight: '35px' }} placeholder="Tìm kiếm theo ghi chú" value={filters.generalSearch} onChange={handleFilterChange} />
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', right: '15px', top: '12px', color: '#999' }}></i>
                    </div>
                </div>

                <div style={{ width: isDesktop ? '32%' : '100%' }}>
                    <select name="status" className="form-input" value={filters.status} onChange={handleFilterChange}>
                        <option value="">Trạng thái</option>
                        <option value="Đang chỉnh sửa">Đang chỉnh sửa</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>
                </div>
            </div>

            <ArticleRewardManagementListing
                data={filteredData}
                filters={filters}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={handleRejectClick}
                onCancelApprove={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onViewDetail={handleViewDetail}
                onRequestEdit={handleOpenRequestEdit}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
            />

            <ArticleRewardManagementForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={() => { fetchData(true); setIsModalOpen(false); }}
                formData={formData}
                setFormData={setFormData}
                users={users}
                isEditing={formData.Id !== 0}
                isReadOnly={isReadOnly}
                allRequests={data}
            />

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa hồ sơ</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Người đề xuất: </strong> {selectedProposalForEdit?.FullName || selectedProposalForEdit?.RequesterName}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="6"
                                        placeholder="Nhập chi tiết các lỗi hoặc minh chứng cần bổ sung"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h3>Xác nhận từ chối hồ sơ</h3>
                            <button className="close-btn" onClick={() => setIsRejectModalOpen(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Lý do từ chối <span style={{ color: 'red' }}>*</span></label>
                                <textarea
                                    className="form-input"
                                    rows="4"
                                    placeholder="Nhập lý do từ chối (sẽ hiển thị cho giảng viên)"
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    style={{ resize: 'vertical' }}
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setIsRejectModalOpen(false)}>Hủy</button>
                            <button className="btn-submit" style={{ backgroundColor: '#e74c3c' }} onClick={confirmReject}>Xác nhận từ chối</button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '650px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Người dùng (Giảng viên đề xuất)</label>
                                <SearchableFocusDropdown
                                    value={exportFilters.userId}
                                    options={userOptions}
                                    onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                    placeholder="Chọn giảng viên"
                                    panelUniqueClass="custom-dropdown-panel-articlereward-export-advanced-filter"
                                    className="custom-dropdown"
                                    filterBy="label,searchKey"
                                    optionLabel="label"
                                />
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th width="150px">Giảng viên</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>SL Bài báo</th>
                                                    <th style={{ textAlign: 'right' }}>Tổng tiền (VNĐ)</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => {
                                                        return (
                                                            <tr key={item.Id}>
                                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                                <td><div style={{ fontWeight: '500', color: '#333' }}>{item.FullName || item.RequesterName || '---'}</div></td>
                                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.ArticleCount || 0}</td>
                                                                <td style={{ textAlign: 'right', color: '#e74c3c', fontWeight: 'bold' }}>{(item.TotalAmount || 0).toLocaleString('vi-VN')}</td>
                                                                <td style={{ textAlign: 'center' }}>{item.Status}</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy bản đăng ký nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleRewardManagement;